import { deiterateAPI } from "../utils/deiterateAPI.util";
import { window } from "../utils/window.util";

export namespace RiskRegister {

    export interface ChangeItem {
        elementName: string;
        newValue: any;
        oldValue: any;
    }

    export interface RiskHistoryItem {
        changedBy: string;
        changedDate: Date;
        changes: {
            [key: string]: ChangeItem;
        };
    }
    export interface RiskEntity {
        riskCat: string;
        riskCriticality: string;
        riskDesc: string;
        riskHistory: RiskHistoryItem[];
        riskID: string;
        riskIDstatus: "active" | "inactive" | "deleted";
        riskLocation: string;
        riskOwner: string;
        riskPublishInPrivacyPolicy: "Yes" | "No";
        riskRisk: string;
        riskSubprocessor: "Yes" | "No";
        riskTeam: string;
        backupLocation: string;
        backupNeeded: "Yes" | "No";
        backupRetention: string;
        classification: string;
        customFields: any[];
        dataStorageLocations: string[];
        dataTypesShared: string[];
        dataTypesSharedString: string;
        dateAdded: Date;
        label: string;
        lastReviewDate: Date;
        legalBits: string;
        nextReviewDate: Date;
        portability: "Yes" | "No";
        securityReview: "Yes" | "No";
        serial: string;
        socCriticality: string;
        socSubserviceOrganisation: string;
        status: "Active" | "Decommissioned" | "Deleted";
        vendorManagementNeeded: "Yes" | "No";
        vendorWebsite: string;
    }

    export interface RiskRegisterResponse {
        statusCode?: number;
        message?: string;
        risks?: RiskEntity[];
        entry?: RiskEntity;
        error?: string | null;
    }

    export class RiskInstance {
        private data: RiskRegister.RiskEntity;

        constructor(data: RiskRegister.RiskEntity) {
            this.data = data;
        }

        // A getter to access the underlying data if needed
        public get entity(): RiskRegister.RiskEntity {
            return this.data;
        }

        // An edit method that calls the static Edit function on the Risk class
        public async edit(updates: Partial<RiskRegister.RiskEntity>): Promise<RiskRegister.RiskRegisterResponse | void> {
            return RiskRegister.RiskProxy.Edit(this.data.riskID, updates);
        }
    }

    export class Risk {

        /**
         * Add an risk to the API.
         * @param id - The ID of the risk to add.
         * @param riskData - Partial risk data to add.
         * @param callback - Optional callback after response.
         */
        static async Add(
            id: string,
            riskData: Partial<RiskEntity>,
            callback?: (res: RiskRegisterResponse) => any
        ): Promise<RiskRegisterResponse | void> {
            console.log("Calling API Add", { companyID: window.companyID, ...riskData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/risks/add/${id}`, {
                mode: 'cors',
                companyID: window.companyID,
                body: JSON.stringify({ companyID: window.companyID, ...riskData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
                throw new Error('Failed to add risk');
            }
        }


        /**
         * Delete an risk from the API.
         * @param id - The ID of the risk to delete.
         * @param callback - Optional callback after response.
         */
        static async Delete(
            id: string,
            callback?: (res: RiskRegisterResponse) => any
        ): Promise<RiskRegisterResponse | void> {
            console.log("Calling API Delete", { companyID: window.companyID });

            // DED-1508: deiterateAPI.POST throws on non-2xx responses, so if we get here, it succeeded
            const res = await deiterateAPI.POST(`/deiterateInternalAPI/risks/delete/${id}`, {
                mode: 'cors',
                companyID: window.companyID,
                body: JSON.stringify({ companyID: window.companyID })
            });

            console.log('API Response', res);
            if (callback) return callback(res);
            return res;
        }


        /**
         * Edit an existing risk in the API.
         * @param id - The ID of the risk to edit.
         * @param riskData - Partial risk data to update.
         * @param callback - Optional callback after response.
         */
        static async Edit(
            id: string,
            riskData: Partial<RiskEntity>,
            callback?: (res: RiskRegisterResponse) => any
        ): Promise<RiskRegisterResponse | void> {
            console.log("Calling API Edit", { companyID: window.companyID, ...riskData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/risks/edit/${id}`, {
                mode: 'cors',
                companyID: window.companyID,
                body: JSON.stringify({ companyID: window.companyID, ...riskData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
                throw new Error('Failed to edit risk');
            }
        }

        /**
         * Fetch all risks from the API.
         */
        static async Get(): Promise<RiskRegister.RiskEntity[] | undefined>;

        /**
         * Fetch a specific risk by ID from the API.
         * @param id - The ID of the risk to fetch.
         */
        static async Get(id: string | number): Promise<RiskInstance | undefined>;
        static async Get(id?: string | number): Promise<RiskRegister.RiskEntity[] | RiskInstance | undefined> {
            if (typeof id === 'undefined' || id == "all") {
                // Fetch all risks
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/risks/get/all`, {
                    mode: 'cors',
                    companyID: window.companyID,
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.risks;
            } else {
                // Fetch single risk
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/risks/get/${id}`, {
                    mode: 'cors',
                    companyID: window.companyID,
                    body: JSON.stringify({ companyID: window.companyID })
                });

                if (res.entry) {
                    return new RiskInstance(res.entry);
                } else {
                    return undefined;
                }
            }
        }

        static async Menu(tenancyId: string): Promise<any> {
            // Fetch all risks
            const res = await deiterateAPI.POST(`/deiterateInternalAPI/risks/get/menu`, {
                mode: 'cors',
                companyID: tenancyId,
                body: JSON.stringify({ companyID: tenancyId })
            });
            return res.menu;
        }

        static async Metadata(): Promise<any> {
            // Fetch all risks
            const res = await deiterateAPI.POST(`/deiterateInternalAPI/risks/get/metadata`, {
                mode: 'cors',
                companyID: window.companyID,
                body: JSON.stringify({ companyID: window.companyID })
            });
            return res.metadata;
        }
    }

    export const RiskProxy = new Proxy(Risk, {
        get(target, prop) {
            const lowerCasedProp = prop.toString().toLowerCase();
            if (lowerCasedProp === 'get') {
                return target.Get;
            } else if (lowerCasedProp === 'add') {
                return target.Add;
            } else if (lowerCasedProp === 'delete') {
                return target.Delete;
            } else if (lowerCasedProp === 'edit') {
                return target.Edit;
            } else if (lowerCasedProp === 'menu') {
                return target.Menu;
            } else if (lowerCasedProp === 'metadata') {
                return target.Metadata;
            }
            return target[lowerCasedProp];
        }
    });
}